import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { usePixelBattle } from './usePixelBattle';
import { useVoiceMessages, VoiceReply } from './VoiceMessages';
import { cooldownDeadline } from './cooldown';
import './App.css';

const PLATFORM_URL = process.env.REACT_APP_PLATFORM_URL || 'http://localhost:3000';
const DEFAULT_AVATAR = process.env.REACT_APP_DEFAULT_AVATAR || 'https://storage.yandexcloud.net/pantheon/icons/user.svg';
const CELL_SIZE = 8;
const MIN_SCALE = 0.04;
const MAX_SCALE = 8;

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
const pointDistance = (first, second) => Math.hypot(first.x - second.x, first.y - second.y);
const pointMiddle = (first, second) => ({ x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 });

const useUserSearch = (query) => {
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!query.trim()) { setResults([]); return undefined; }
    const timer = window.setTimeout(() => {
      api(`/users/search?query=${encodeURIComponent(query)}`)
        .then((data) => setResults(Array.isArray(data) ? data : []))
        .catch(() => setResults([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  return results;
};

const Header = ({ user, onlineCount }) => (
  <header className="platform-header">
    <div className="platform-header-inner">
      <a className="platform-logo" href={PLATFORM_URL}><strong>Пантеон</strong><span>beta</span></a>
      <div className="platform-header-right">
        {onlineCount !== null && <span className="event-online"><i />{onlineCount.toLocaleString('ru-RU')} онлайн</span>}
        {user ? <a className="platform-avatar" href={`${PLATFORM_URL}/${user.login}`} title={user.login}><img src={user.profile_image_url || DEFAULT_AVATAR} alt={user.display_name || user.login} /></a> : <><a className="platform-button secondary" href={PLATFORM_URL}>Войти</a><a className="platform-button primary" href={PLATFORM_URL}>Регистрация</a></>}
      </div>
    </div>
  </header>
);

function App() {
  const [user, setUser] = useState(null);
  const [leaderboardType, setLeaderboardType] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);
  const [overwriteNotices, setOverwriteNotices] = useState([]);
  const [voiceNotices, setVoiceNotices] = useState([]);
  const userRef = useRef(null);
  const noticeTimersRef = useRef(new Map());

  const handlePixelUpdate = useCallback((pixel) => {
    const currentUserID = Number(userRef.current?.user_id || 0);
    const authorID = Number(pixel?.user_id || 0);
    if (!currentUserID || Number(pixel?.previous_user_id || 0) !== currentUserID || authorID === currentUserID) return;

    const noticeID = `${pixel.sequence}-${authorID}`;
    api(`/users/id/${authorID}`).catch(() => ({ user_id: authorID, login: `user-${authorID}`, display_name: `Пользователь #${authorID}` }))
      .then((author) => {
        setOverwriteNotices((current) => [...current.filter((notice) => notice.id !== noticeID), { id: noticeID, author, x: pixel.x, y: pixel.y, sequence: pixel.sequence }].slice(-3));
        const timer = window.setTimeout(() => {
          setOverwriteNotices((current) => current.filter((notice) => notice.id !== noticeID));
          noticeTimersRef.current.delete(noticeID);
        }, 7000);
        noticeTimersRef.current.set(noticeID, timer);
      });
  }, []);

  const handleVoiceMessage = useCallback((message) => {
    api(`/users/id/${message.sender_user_id}`).catch(() => ({ user_id: message.sender_user_id, login: `user-${message.sender_user_id}`, display_name: `Пользователь #${message.sender_user_id}` }))
      .then((author) => setVoiceNotices((current) => [...current.filter((notice) => notice.message_id !== message.message_id), { ...message, author }].slice(-3)));
  }, []);

  const { event, palette, pixels, leaderboard, onlineCount, isLoading, error, joinTeam, placePixel } = usePixelBattle(handlePixelUpdate, leaderboardType);
  useVoiceMessages(Boolean(user), handleVoiceMessage);
  const canvasRef = useRef(null);
  const viewportRef = useRef(null);
  const viewRef = useRef({ x: 0, y: 0, scale: 1 });
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null);
  const fittedCanvasRef = useRef(null);

  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedPixel, setSelectedPixel] = useState(null);
  const [query, setQuery] = useState('');
  const [actionError, setActionError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [view, setView] = useState(viewRef.current);
  const [panelsOpen, setPanelsOpen] = useState(true);
  const searchResults = useUserSearch(query);

  const canvas = event?.canvas;
  const worldWidth = (canvas?.width || 0) * CELL_SIZE;
  const worldHeight = (canvas?.height || 0) * CELL_SIZE;

  const commitView = useCallback((next) => {
    viewRef.current = next;
    setView(next);
  }, []);

  const fitCanvas = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || !worldWidth || !worldHeight) return;
    const rect = viewport.getBoundingClientRect();
    const scale = clamp(Math.min((rect.width - 96) / worldWidth, (rect.height - 96) / worldHeight), MIN_SCALE, 1.35);
    commitView({ x: (rect.width - worldWidth * scale) / 2, y: (rect.height - worldHeight * scale) / 2, scale });
  }, [commitView, worldHeight, worldWidth]);

  useEffect(() => {
    api('/users/me').then((currentUser) => { userRef.current = currentUser; setUser(currentUser); }).catch(() => { userRef.current = null; setUser(null); });
  }, []);
  useEffect(() => {
    if (!user) { setTeamMembers([]); return undefined; }
    let active = true;
    const loadMembers = () => api('/pixel-battle/team/members')
      .then((items) => { if (active) setTeamMembers(Array.isArray(items) ? items : []); })
      .catch(() => { if (active) setTeamMembers([]); });
    loadMembers();
    const timer = window.setInterval(loadMembers, 15000);
    return () => { active = false; window.clearInterval(timer); };
  }, [user]);
  useEffect(() => () => { noticeTimersRef.current.forEach((timer) => window.clearTimeout(timer)); }, []);
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(timer); }, []);
  useEffect(() => { if (selectedColor === null && palette.length) setSelectedColor(palette[0].code); }, [palette, selectedColor]);

  useLayoutEffect(() => {
    if (!canvas?.id || fittedCanvasRef.current === canvas.id) return;
    fittedCanvasRef.current = canvas.id;
    fitCanvas();
  }, [canvas?.id, fitCanvas]);

  const colors = useMemo(() => new Map(palette.map((color) => [color.code, [color.red, color.green, color.blue]])), [palette]);
  useEffect(() => {
    const element = canvasRef.current;
    if (!element || !canvas?.width || !canvas?.height || pixels.length < canvas.width * canvas.height) return;
    element.width = canvas.width;
    element.height = canvas.height;
    const context = element.getContext('2d');
    const image = context.createImageData(canvas.width, canvas.height);
    for (let index = 0; index < canvas.width * canvas.height; index += 1) {
      const color = colors.get(pixels[index]) || [255, 255, 255];
      const offset = index * 4;
      image.data[offset] = color[0]; image.data[offset + 1] = color[1]; image.data[offset + 2] = color[2]; image.data[offset + 3] = 255;
    }
    context.putImageData(image, 0, 0);
  }, [canvas?.height, canvas?.width, colors, pixels]);

  const localPoint = useCallback((clientX, clientY) => {
    const rect = viewportRef.current.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const selectAt = useCallback((point) => {
    if (!canvas) return;
    const current = viewRef.current;
    const x = Math.floor((point.x - current.x) / current.scale / CELL_SIZE);
    const y = Math.floor((point.y - current.y) / current.scale / CELL_SIZE);
    if (x >= 0 && y >= 0 && x < canvas.width && y < canvas.height) {
      setSelectedPixel({ x, y });
      setActionError('');
    }
  }, [canvas]);

  const handlePointerDown = (pointerEvent) => {
    pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
    const point = localPoint(pointerEvent.clientX, pointerEvent.clientY);
    pointersRef.current.set(pointerEvent.pointerId, point);
    const points = [...pointersRef.current.values()];
    if (points.length === 1) {
      gestureRef.current = { type: 'pan', pointerID: pointerEvent.pointerId, start: point, view: viewRef.current, moved: false };
    } else if (points.length === 2) {
      const middle = pointMiddle(points[0], points[1]);
      const current = viewRef.current;
      gestureRef.current = { type: 'pinch', distance: pointDistance(points[0], points[1]), scale: current.scale, worldX: (middle.x - current.x) / current.scale, worldY: (middle.y - current.y) / current.scale };
    }
  };

  const handlePointerMove = (pointerEvent) => {
    if (!pointersRef.current.has(pointerEvent.pointerId)) return;
    pointersRef.current.set(pointerEvent.pointerId, localPoint(pointerEvent.clientX, pointerEvent.clientY));
    const points = [...pointersRef.current.values()];
    const gesture = gestureRef.current;
    if (points.length === 2 && gesture?.type === 'pinch') {
      const middle = pointMiddle(points[0], points[1]);
      const scale = clamp(gesture.scale * pointDistance(points[0], points[1]) / Math.max(gesture.distance, 1), MIN_SCALE, MAX_SCALE);
      commitView({ x: middle.x - gesture.worldX * scale, y: middle.y - gesture.worldY * scale, scale });
    } else if (points.length === 1 && gesture?.type === 'pan') {
      const dx = points[0].x - gesture.start.x;
      const dy = points[0].y - gesture.start.y;
      if (Math.hypot(dx, dy) > 4) gesture.moved = true;
      commitView({ ...gesture.view, x: gesture.view.x + dx, y: gesture.view.y + dy });
    }
  };

  const handlePointerUp = (pointerEvent) => {
    const point = pointersRef.current.get(pointerEvent.pointerId);
    const gesture = gestureRef.current;
    const wasOnlyPointer = pointersRef.current.size === 1;
    pointersRef.current.delete(pointerEvent.pointerId);
    if (wasOnlyPointer && gesture?.type === 'pan' && !gesture.moved && point) selectAt(point);
    const remaining = [...pointersRef.current.entries()];
    if (remaining.length === 1) gestureRef.current = { type: 'pan', pointerID: remaining[0][0], start: remaining[0][1], view: viewRef.current, moved: true };
    else if (!remaining.length) gestureRef.current = null;
  };

  const zoomAt = useCallback((point, factor) => {
    const current = viewRef.current;
    const scale = clamp(current.scale * factor, MIN_SCALE, MAX_SCALE);
    const worldX = (point.x - current.x) / current.scale;
    const worldY = (point.y - current.y) / current.scale;
    commitView({ x: point.x - worldX * scale, y: point.y - worldY * scale, scale });
  }, [commitView]);

  const handleWheel = (wheelEvent) => {
    wheelEvent.preventDefault();
    zoomAt(localPoint(wheelEvent.clientX, wheelEvent.clientY), Math.exp(-wheelEvent.deltaY * 0.0015));
  };

  const cooldown = Math.max(0, Math.ceil((cooldownDeadline(event) - now) / 1000));
  const selectedRGB = colors.get(selectedColor) || [0, 0, 0];
  const canConfirm = user && event?.team_captain_id && canvas?.is_active && !cooldown && selectedPixel && selectedColor !== null;

  const confirmPixel = async () => {
    if (!user) { setActionError('Войдите в Pantheon, чтобы ставить пиксели'); return; }
    if (!event?.team_captain_id) { setActionError('Сначала выберите команду'); return; }
    if (!canvas?.is_active) { setActionError('Событие сейчас не активно'); return; }
    if (cooldown) { setActionError(`Следующий пиксель можно поставить через ${cooldown} сек.`); return; }
    if (!selectedPixel || selectedColor === null) return;
    try {
      await placePixel(selectedPixel.x, selectedPixel.y, selectedColor);
      setSelectedPixel(null);
      setActionError('');
    } catch (requestError) { setActionError(requestError.message); }
  };

  const keepNoticeOpen = (noticeID) => {
    const timer = noticeTimersRef.current.get(noticeID);
    if (timer) window.clearTimeout(timer);
    noticeTimersRef.current.delete(noticeID);
  };

  return (
    <div className="pb-app">
      <Header user={user} onlineCount={onlineCount} />
      <main className="battle-workspace">
        {isLoading ? <div className="pb-state"><div className="pb-loader" /><p>Загружаем полотно…</p></div> : error ? <div className="pb-state error"><h2>Не удалось открыть Pixel Battle</h2><p>{error}</p></div> : !canvas ? <div className="pb-state"><h2>Событие скоро начнётся</h2><p>Администраторы пока не создали полотно.</p></div> : <>
          <div ref={viewportRef} className="pb-canvas-viewport" onWheel={handleWheel} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp}>
            <div className="pb-canvas-stage" style={{ width: worldWidth, height: worldHeight, transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})` }}>
              <canvas ref={canvasRef} className="pb-canvas" style={{ width: worldWidth, height: worldHeight }} aria-label="Полотно Pixel Battle" />
              {selectedPixel && <span className="pixel-preview" style={{ left: selectedPixel.x * CELL_SIZE, top: selectedPixel.y * CELL_SIZE, width: CELL_SIZE, height: CELL_SIZE, background: `rgba(${selectedRGB.join(',')},.48)` }} />}
            </div>
          </div>

          <div className="canvas-toolbar">
            <button onClick={() => zoomAt({ x: viewportRef.current.clientWidth / 2, y: viewportRef.current.clientHeight / 2 }, 1 / 1.25)} aria-label="Отдалить">−</button>
            <span>{Math.round(view.scale * 100)}%</span>
            <button onClick={() => zoomAt({ x: viewportRef.current.clientWidth / 2, y: viewportRef.current.clientHeight / 2 }, 1.25)} aria-label="Приблизить">+</button>
            <button onClick={fitCanvas} aria-label="Показать всё полотно">⌗</button>
          </div>

          <button className={`panels-toggle ${panelsOpen ? 'open' : ''}`} onClick={() => setPanelsOpen((value) => !value)}>{panelsOpen ? 'Скрыть панели ›' : '‹ Команда и лидеры'}</button>
          <aside className={`battle-panels ${panelsOpen ? 'open' : ''}`}>
            <section className="battle-panel team-panel"><div className="panel-heading"><div><small>Твоя сторона</small><h2>Команда</h2></div>{event.team_captain_id && <i />}</div>{event.team_captain_id ? <div className="selected-team"><strong>{event.team_captain_login ? `@${event.team_captain_login}` : `Команда #${event.team_captain_id}`}</strong><span>Каждый установленный пиксель увеличивает её счёт на 2, если капитан ведёт трансляцию и на 1 в остальное время.</span></div> : user ? <><p>Выбери капитана один раз. Сменить команду во время события нельзя.</p><input value={query} onChange={(input) => setQuery(input.target.value)} placeholder="Логин стримера" /><div className="team-search">{searchResults.slice(0, 5).map((result) => <button key={result.user_id} onClick={async () => { try { await joinTeam(result.user_id); setQuery(''); } catch (requestError) { setActionError(requestError.message); } }}><span>{result.display_name || result.login}</span><small>@{result.login}</small></button>)}</div></> : <div className="login-prompt"><p>Войди в Pantheon, чтобы выбрать команду.</p><a href={PLATFORM_URL}>Войти</a></div>}{teamMembers.length > 0 && <div className="team-members"><div className="team-members-heading"><strong>Участники моей команды</strong><b>{teamMembers.length}</b></div><ol>{teamMembers.map((member) => <li key={member.user_id}><span>{member.login ? `@${member.login}` : `Пользователь #${member.user_id}`}</span><strong>{member.pixels_placed.toLocaleString('ru-RU')} пикс.</strong></li>)}</ol></div>}</section>
            <section className="battle-panel leaderboard-panel"><div className="panel-heading"><div><small>{leaderboardType === 0 ? 'Гонка сообществ' : 'Личный зачёт'}</small><h2>Лидеры</h2></div><b>{leaderboard.length}</b></div><div className="leaderboard-tabs" role="group" aria-label="Тип лидерборда"><button className={leaderboardType === 0 ? 'active' : ''} onClick={() => setLeaderboardType(0)}>Команды</button><button className={leaderboardType === 1 ? 'active' : ''} onClick={() => setLeaderboardType(1)}>Пользователи</button></div><ol>{leaderboard.slice(0, 10).map((item, index) => <li key={item.user_id}><em>{index + 1}</em><span>{item.login ? `@${item.login}` : `${leaderboardType === 0 ? 'Команда' : 'Пользователь'} #${item.user_id}`}</span><strong>{item.pixels_placed.toLocaleString('ru-RU')}</strong></li>)}</ol>{!leaderboard.length && <p>Рейтинг появится после первого пикселя.</p>}</section>
          </aside>

          <div className="bottom-controls">
            {selectedPixel && <div className="placement-confirm"><span style={{ background: `rgb(${selectedRGB.join(',')})` }} /><div><small>Выбран пиксель</small><strong>{selectedPixel.x}, {selectedPixel.y}</strong></div><button disabled={!canConfirm} onClick={confirmPixel}>{cooldown ? `${cooldown} сек.` : 'Установить пиксель'}</button><button className="cancel-selection" onClick={() => setSelectedPixel(null)} aria-label="Отменить выбор">×</button></div>}
            <div className="palette-panel" aria-label="Палитра цветов">{palette.map((color) => <button key={color.code} className={selectedColor === color.code ? 'selected' : ''} style={{ background: `rgb(${color.red},${color.green},${color.blue})` }} onClick={() => setSelectedColor(color.code)} aria-label={`Цвет ${color.code}`} />)}</div>
            {actionError && <p className="placement-error">{actionError}</p>}
          </div>
        </>}
        <div className="overwrite-notices" aria-live="polite" aria-label="Уведомления о пикселях">
          {voiceNotices.map((notice) => <article key={notice.message_id} className="overwrite-notice incoming-voice-notice">
            <a className="overwrite-notice-avatar" href={`${PLATFORM_URL}/${encodeURIComponent(notice.author.login)}`} target="_blank" rel="noreferrer"><img src={notice.author.profile_image_url || DEFAULT_AVATAR} alt="" /></a>
            <div className="overwrite-notice-content">
              <a href={`${PLATFORM_URL}/${encodeURIComponent(notice.author.login)}`} target="_blank" rel="noreferrer"><strong>{notice.author.display_name || notice.author.login}</strong><small>@{notice.author.login}</small></a>
              <p>ответил голосом на пиксель <b>{notice.x}, {notice.y}</b></p>
              <audio src={`data:${notice.mime_type};base64,${notice.audio}`} controls preload="metadata" />
            </div>
            <button onClick={() => setVoiceNotices((current) => current.filter((item) => item.message_id !== notice.message_id))} aria-label="Закрыть голосовое">×</button>
          </article>)}
          {overwriteNotices.map((notice) => <article key={notice.id} className="overwrite-notice">
            <a className="overwrite-notice-avatar" href={`${PLATFORM_URL}/${encodeURIComponent(notice.author.login)}`} target="_blank" rel="noreferrer">
              <img src={notice.author.profile_image_url || DEFAULT_AVATAR} alt="" />
            </a>
            <div className="overwrite-notice-content">
              <a href={`${PLATFORM_URL}/${encodeURIComponent(notice.author.login)}`} target="_blank" rel="noreferrer"><strong>{notice.author.display_name || notice.author.login}</strong><small>@{notice.author.login}</small></a>
              <p>закрасил ваш пиксель <b>{notice.x}, {notice.y}</b></p>
              <VoiceReply sequence={notice.sequence} onInteraction={() => keepNoticeOpen(notice.id)} onSent={() => setOverwriteNotices((current) => current.filter((item) => item.id !== notice.id))} />
            </div>
            <button onClick={() => setOverwriteNotices((current) => current.filter((item) => item.id !== notice.id))} aria-label="Закрыть уведомление">×</button>
          </article>)}
        </div>
      </main>
    </div>
  );
}

export default App;
